import { useEffect, useState } from "react";
import type { ForYouFeedItem } from "../../forYou/hooks/useGetForYouRecommendedContentQuery";
import { displayToast } from "../../../components/app/AppToast";
import { HapticEffect, SoundEffect } from "../../../interfaces";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import {
  convertArticleToItemData,
  gaEventArticleLike,
} from "../../../tools/analytics/domains/article.analytics";
import { gaEventContentLike } from "../../../tools/analytics/domains/content.analytics";
import { iosGenerateHaptic, iosPlaySound } from "../../../tools/ios";
import {
  useCurrentUser,
  replaceCurrentUserData,
  type CurrentUser,
} from "./useCurrentUser";
import { filterArrayDuplicates } from "../../app/utils/filterArrayDuplicates";
import { useOnboardingGuard } from "./useOnboardingGuard";
import { useTranslation } from "react-i18next";
import { apiClient, useApiRevalidate } from "../../../services/api";
import type { SearchArticleItem } from "../../../features/search/hooks/useSearchArticlesQuery";
import type {
  ArticleDetail,
  RelatedArticleCard,
} from "../../../features/article/hooks/useArticleData";

type FeedArticleItem = Extract<ForYouFeedItem, { content_format: "Article" }>;

export function useLikeArticle(
  article:
    | ArticleDetail
    | RelatedArticleCard
    | SearchArticleItem
    | FeedArticleItem
    | null,
  analyticsListName: AnalyticsItemList
) {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const revalidate = useApiRevalidate();
  const [isReady, setReady] = useState(false);
  const likesIds = currentUser?.likes ?? [];
  const isLikedStore = article ? likesIds.includes(article.id) : false;
  const [isLikedOptimistic, setLikedOptimistic] = useState(isLikedStore);
  const [isFetching, setFetching] = useState(false);
  const onboardingGuard = useOnboardingGuard();

  // Setup hook and state once article is set
  useEffect(() => {
    if (article) {
      setLikedOptimistic(isLikedStore);
      setReady(true);
    }
  }, [article, isLikedStore]);

  useEffect(() => {
    if (
      isReady &&
      !isFetching &&
      isLikedOptimistic !== isLikedStore &&
      article !== null &&
      currentUser !== null
    ) {
      updateStatus(article, currentUser);
    }
  }, [isLikedOptimistic, isLikedStore]);

  async function toggleLike(
    article:
      ArticleDetail | RelatedArticleCard | SearchArticleItem | FeedArticleItem
  ) {
    try {
      if (
        !onboardingGuard(
          t("🔒 Please complete your profile first to like this article. ")
        )
      ) {
        return;
      }

      setLikedOptimistic(!isLikedOptimistic);
      iosGenerateHaptic(HapticEffect.SUCCESS);
      displayToast(
        isLikedOptimistic
          ? t("Removed from your liked articles")
          : t("Article liked. Added to your profile."),
        "success"
      );

      if (!isLikedOptimistic) {
        iosPlaySound(SoundEffect.LIKE);
        gaEventArticleLike({
          article,
          listName: analyticsListName,
        });
        gaEventContentLike(
          convertArticleToItemData({ article, listName: analyticsListName })
        );
      }
    } catch (error) {
      console.error("Couldn't like/unlike article.", error);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  async function updateStatus(
    article:
      ArticleDetail | RelatedArticleCard | SearchArticleItem | FeedArticleItem,
    currentUser: CurrentUser
  ) {
    try {
      const articleId = article.id;
      setFetching(true);

      if (isLikedOptimistic) {
        await apiClient.post("/webapp/private/articles/:id/likes", {
          param: { id: articleId },
        });
      } else {
        await apiClient.delete("/webapp/private/articles/:id/likes", {
          param: { id: articleId },
        });
      }

      const likes = isLikedOptimistic
        ? filterArrayDuplicates([...currentUser.likes, articleId])
        : currentUser.likes.filter((id) => id !== articleId);
      replaceCurrentUserData({ ...currentUser, likes });

      void revalidate("/webapp/public/articles/:slug", {
        param: { slug: article.slug },
      });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    } finally {
      setFetching(false);
    }
  }

  return {
    isLiked: isLikedOptimistic,
    toggleLike,
  };
}
